import { useEffect, useState, useCallback, useRef } from 'react';
import { socketService, AIResponse, TopicActivated, SocketError } from '../services/SocketService';

export interface SocketState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  socketId?: string;
  reconnectAttempts: number;
}

export interface UseSocketOptions {
  autoConnect?: boolean;
  token?: string;
}

/**
 * Socket.IO连接管理Hook
 * 根据设计文档的WebSocket客户端集成实现
 */
export const useSocket = (options: UseSocketOptions = {}) => {
  const { autoConnect = true, token } = options;
  
  const [state, setState] = useState<SocketState>({
    connected: false,
    connecting: false,
    error: null,
    reconnectAttempts: 0,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * 更新状态
   */
  const updateState = useCallback((updates: Partial<SocketState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * 连接到服务器
   */
  const connect = useCallback(async (authToken?: string) => {
    if (stateRef.current.connecting || stateRef.current.connected) {
      return;
    }

    const tokenToUse = authToken || token;
    
    // 验证token格式
    const isValidJWT = (token: string | null) => {
      if (!token || typeof token !== 'string' || token === 'undefined' || token === 'null' || token.trim() === '') {
        return false;
      }
      return token.split('.').length === 3;
    };
    
    if (!tokenToUse || !isValidJWT(tokenToUse)) {
      console.log('No valid token available, skipping socket connection', { 
        hasToken: !!tokenToUse, 
        tokenPreview: tokenToUse ? tokenToUse.substring(0, 20) + '...' : 'none' 
      });
      return;
    }

    updateState({ connecting: true, error: null });

    try {
      // 设置认证token
      socketService.updateToken(tokenToUse);
      
      socketService.connect();
      const info = socketService.getConnectionInfo();
      updateState({
        connected: true,
        connecting: false,
        socketId: info.socketId,
      });
    } catch (error: any) {
      updateState({
        connected: false,
        connecting: false,
        error: error.message || 'Connection failed',
      });
      throw error;
    }
  }, [token, updateState]);

  /**
   * 断开连接
   */
  const disconnect = useCallback(() => {
    socketService.disconnect();
    updateState({
      connected: false,
      connecting: false,
      error: null,
      socketId: undefined,
      reconnectAttempts: 0,
    });
  }, [updateState]);

  /**
   * 发送消息
   */
  const sendMessage = useCallback((data: {
    sessionId?: string;
    message: string;
    model?: 'gemini' | 'deepseek';
    topicType?: 'image' | 'text';
    messageType?: 'text' | 'image';
    imageData?: {
      url: string;
      base64: string;
      filename: string;
      size: number;
      mimeType: string;
    };
  }) => {
    if (!stateRef.current.connected) {
      throw new Error('Socket not connected');
    }
    socketService.sendMessage(data);
  }, []);

  /**
   * 加入话题房间
   */
  const joinTopic = useCallback((topicId: string) => {
    if (!stateRef.current.connected) {
      throw new Error('Socket not connected');
    }
    socketService.joinTopic(topicId);
  }, []);

  /**
   * 会话继续响应
   */
  const sessionContinueResponse = useCallback((topicId: string, confirmed: boolean) => {
    if (!stateRef.current.connected) {
      throw new Error('Socket not connected');
    }
    socketService.sessionContinueResponse(topicId, confirmed);
  }, []);

  /**
   * 提交评价
   */
  const submitRating = useCallback((data: {
    topicId: string;
    rating?: number;
    feedback?: string;
    skipped?: boolean;
  }) => {
    if (!stateRef.current.connected) {
      throw new Error('Socket not connected');
    }
    socketService.submitRating(data);
  }, []);

  /**
   * 添加事件监听器
   */
  const on = useCallback(<T = any>(event: string, callback: (data: T) => void) => {
    socketService.on(event, callback);
  }, []);

  /**
   * 移除事件监听器
   */
  const off = useCallback((event: string, callback?: (...args: any[]) => void) => {
    if (callback) {
      socketService.off(event, callback);
    }
  }, []);

  /**
   * 更新认证令牌
   */
  const updateToken = useCallback((newToken: string) => {
    socketService.updateToken(newToken);
  }, []);

  // 设置事件监听器
  useEffect(() => {
    const handleConnect = () => {
      const info = socketService.getConnectionInfo();
      updateState({
        connected: true,
        connecting: false,
        error: null,
        socketId: info.socketId,

      });
    };

    const handleDisconnect = () => {
      updateState({
        connected: false,
        socketId: undefined,
      });
    };

    const handleConnectError = (error: Error) => {
      updateState({
        connected: false,
        connecting: false,
        error: error.message,
      });
    };

    const handleReconnectFailed = () => {
      updateState({
        connected: false,
        connecting: false,
        error: 'Reconnection failed after maximum attempts',
      });
    };

    socketService.on('connect', handleConnect);
    socketService.on('disconnect', handleDisconnect);
    socketService.on('connect_error', handleConnectError);
    socketService.on('reconnect_failed', handleReconnectFailed);

    return () => {
      socketService.off('connect', handleConnect);
      socketService.off('disconnect', handleDisconnect);
      socketService.off('connect_error', handleConnectError);
      socketService.off('reconnect_failed', handleReconnectFailed);
    };
  }, [updateState]);

  // 自动连接 - 当token可用时
  useEffect(() => {
    if (autoConnect && token && !state.connected && !state.connecting) {
      connect().catch(() => {
        // Auto-connect failed, will retry automatically
      });
    }
  }, [autoConnect, token, connect, state.connected, state.connecting]);

  // 清理 - 只在应用完全卸载时断开连接，不在组件切换时断开
  // useEffect(() => {
  //   return () => {
  //     if (socketService.isConnected()) {
  //       socketService.disconnect();
  //     }
  //   };
  // }, []);

  return {
    // 状态
    ...state,
    
    // 方法
    connect,
    disconnect,
    sendMessage,
    joinTopic,
    sessionContinueResponse,
    submitRating,
    on,
    off,
    updateToken,
  };
};

/**
 * 专门用于监听AI响应的Hook
 */
export const useAIResponse = (callback: (response: AIResponse) => void) => {
  useEffect(() => {
    socketService.on('ai_response', callback);
    return () => {
      socketService.off('ai_response', callback);
    };
  }, [callback]);
};

/**
 * 专门用于监听话题激活的Hook
 */
export const useTopicActivated = (callback: (data: TopicActivated) => void) => {
  useEffect(() => {
    socketService.on('topic_activated', callback);
    return () => {
      socketService.off('topic_activated', callback);
    };
  }, [callback]);
};

/**
 * 专门用于监听Socket错误的Hook
 */
export const useSocketError = (callback: (error: SocketError) => void) => {
  useEffect(() => {
    socketService.on('error', callback);
    return () => {
      socketService.off('error', callback);
    };
  }, [callback]);
};